import * as React from 'react';
import { cn } from '@shared/lib/utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  icon?: React.ReactNode;
  error?: string;
  invalid?: boolean;
}

const inputFieldClassName = cn(
  'flex h-10 w-full rounded-lg border border-base-border bg-background px-4 py-1 text-sm leading-5 text-base-foreground shadow-xs',
  'placeholder:text-base-muted-foreground',
  'transition-[color,box-shadow,border-color] duration-200',
  'focus-visible:outline-none focus-visible:border-[#A3A3A3] focus-visible:shadow-[0_0_0_3px_rgba(163,163,163,0.5)]',
  'disabled:cursor-not-allowed disabled:opacity-50',
);

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, icon, error, invalid, ...props }, ref) => {
    const hasError = Boolean(error || invalid);

    return (
      <div className="w-full">
        <div className="relative">
          {icon && (
            <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base-muted-foreground">
              {icon}
            </div>
          )}
          <input
            type={type}
            className={cn(
              inputFieldClassName,
              icon && 'pl-10',
              hasError && [
                'border-destructive shadow-xs',
                'focus-visible:border-destructive focus-visible:shadow-[0_0_0_3px_rgba(220,38,38,0.2)]',
              ],
              className,
            )}
            ref={ref}
            aria-invalid={hasError || undefined}
            {...props}
          />
        </div>
        {error && <p className="mt-2 text-sm leading-5 text-destructive">{error}</p>}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
